import { useUser } from '@auth0/nextjs-auth0'
import {
  Box,
  Button,
  CardHeader,
  Container,
  Divider,
  Grid,
  Typography,
} from '@mui/material'
import Head from 'next/head'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import { useContext, useEffect } from 'react'

import { Alert } from '../components/Alert'
import { Copyright } from '../components/copyright/copyright'
import { LoginLayout } from '../components/layout/login-layout'
import { Logo } from '../components/Logo'
import { PageLoading } from '../components/page-loading/page-loading'

import redirectToAccount from '../utils/pages/redirectToAccount'
import UserRoleContext from '../utils/pages/UserRolesContext'
import { trackEvent } from '../utils/tracker'

const Root = () => {
  const { user, error, isLoading } = useUser()
  const userRole: string[] = useContext(UserRoleContext)

  const router = useRouter()

  useEffect(() => {
    if (user) {
      trackEvent('Signed in', { type: 'account', user })
      redirectToAccount(router)
    }
  }, [user, userRole])

  if (isLoading) return <PageLoading />

  return (
    <LoginLayout>
      <Head>
        <title>Infyos Platform</title>
        <meta name="robots" content="noindex" />
        <meta name="googlebot" content="noindex" />
      </Head>
      <Box
        component="main"
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexGrow: 1,
          minHeight: '100%',
        }}
      >
        <Container>
          {error ? (
            <Box my={1}>
              <Alert severity="error" title="Error" message={error.message} />
            </Box>
          ) : null}

          {!user && (
            <Grid container component="main" sx={{ height: '100vh' }}>
              <Grid
                item
                xs={false}
                sm={4}
                md={7}
                sx={{
                  backgroundImage:
                    'url(https://images.pexels.com/photos/1473090/pexels-photo-1473090.jpeg)',
                  backgroundRepeat: 'no-repeat',
                  backgroundColor: (t) => {
                    return t.palette.mode === 'light'
                      ? t.palette.grey[50]
                      : t.palette.grey[900]
                  },
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
              <Grid item xs={12} sm={8} md={5}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Logo variant="dark" />
                </Box>
                <Box
                  sx={{
                    my: 6,
                    mx: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="body1">
                    Infyos is a supply chain sustainability platform that lets
                    battery supply chain players manage, measure and improve
                    their sustainability.
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <NextLink href="/api/auth/login" passHref>
                      <Button
                        data-cy="login-button"
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                      >
                        Login to Infyos Platform
                      </Button>
                    </NextLink>
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Divider />
                    <CardHeader
                      title="Invite only"
                      subheader="Please contact info@infyos.com to discuss how we can help with your supply chain sustainability journey."
                    />
                    <Copyright />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
        </Container>
      </Box>
    </LoginLayout>
  )
}

export default Root
